<template>
	<header class="one-header">
		<ModalKeyboardShortcuts
			:modal-name="modalNameKeyboardShortcuts"
		/>
		<div class="one-header__left">
			<img
				src="/images/asport_icon_light.svg"
				alt="Logo"
				class="one-header__logo"
			>
			<h1 class="one-header__title">
				ONE
			</h1>
		</div>
		<ul
			v-if="navItems.length > 1"
			class="one-header__navigation-list"
			:class="{
				'is-mobile-menu-open': mobileMenuOpen,
			}"
		>
			<li
				v-for="item in navItems"
				:key="item.label"
				class="one-header__navigation-item"
				:class="{
					'is-active': item.isActive,
				}"
			>
				<router-link
					:to="item.route"
					class="one-header__navigation-link"
				>
					{{ item.label }}
				</router-link>
			</li>
		</ul>
		<div class="one-header__right">
			<Button
				v-if="!isMobileViewport && (isTaggingTool || isCaptureTool)"
				class="one-header__help-button"
				size="medium"
				theme="dark"
				modifier="borderless"
				text="?"
				@click="openHelp"
			/>
			<Button
				v-if="!isMobileViewport && isTaggingTool"
				size="medium"
				theme="dark"
				modifier="borderless"
				:icon="isTaggingToolSettingsOpen ? 'e-remove' : 'settings-gear'"
				@click="handleSettingsClick"
			/>
			<Button
				v-if="isMobileViewport && navItems.length > 1"
				size="medium"
				theme="dark"
				modifier="borderless"
				:icon="mobileMenuOpen ? 'e-remove' : 'menu'"
				@click="handleMenuClick"
			/>
		</div>
	</header>
</template>

<script>
import Button from '@shared/components/Button';
import ModalKeyboardShortcuts from '@shared/components/ModalKeyboardShortcuts.vue';
import { mapActions, mapGetters } from 'vuex';
import { ROUTE_NAMES } from '@videotools/js/one/router';
import { taggingToolKeyActions } from '@videotools/js/constants/keyboardActions';
import { KEYBOARD_SHORTCUTS as MULTI_FORMAT_KEYBOARD_SHORTCUTS } from '@shared/components/MultiFormatTool/MultiFormatTool.vue';

export default {
	name:       'Header',
	components: { Button, ModalKeyboardShortcuts },
	data() {
		return {
			mobileMenuOpen:             false,
			modalNameKeyboardShortcuts: 'ModalKeyboardShortcuts',
		};
	},
	computed: {
		...mapGetters({
			isTaggingToolSettingsOpen: 'app/isTaggingToolSettingsOpen',
			isMobileViewport:          'device/isMobileViewport',
			isDownloadable:            'event/isDownloadable',
			videoAvailable:            'event/videoAvailable',
			canViewTagCenter:          'permissions/canViewTagCenter',
			canUseHighlightTool:       'permissions/canUseHighlightTool',
			canUseMultiFormatTool:     'permissions/canUseMultiFormatTool',
			canUseAnalyzeTool:         'permissions/canUseAnalyzeTool',
		}),
		navItems() {
			const items = [];
			const eventId = this.$route.params.id;

			if (this.canViewTagCenter && this.videoAvailable) {
				items.push({
					label:    'Tag',
					route:    { name: ROUTE_NAMES.TAGGING_TOOL, params: { id: eventId } },
					isActive: this.$route.name === ROUTE_NAMES.TAGGING_TOOL,
				});
			}
			if (this.canUseHighlightTool && this.videoAvailable && !window.isAtv) {
				items.push({
					label:    'Highlight',
					route:    { name: ROUTE_NAMES.HIGHLIGHT, params: { id: eventId } },
					isActive: this.$route.name === ROUTE_NAMES.HIGHLIGHT,
				});
			}
			if (this.canUseMultiFormatTool && this.videoAvailable) {
				items.push({
					label:    'Capture',
					route:    { name: ROUTE_NAMES.CAPTURE, params: { id: eventId } },
					isActive: this.$route.name === ROUTE_NAMES.CAPTURE,
				});
			}
			if (this.canUseAnalyzeTool) {
				items.push({
					label:    'Analyze',
					route:    { name: ROUTE_NAMES.ANALYZE, params: { id: eventId } },
					isActive: this.$route.name === ROUTE_NAMES.ANALYZE,
				});
			}

			return items;
		},
		isTaggingTool() {
			return this.$route.name === ROUTE_NAMES.TAGGING_TOOL;
		},
		isCaptureTool() {
			return this.$route.name === ROUTE_NAMES.CAPTURE;
		},
	},
	watch: {
		$route() {
			this.mobileMenuOpen = false;
		},
	},
	methods: {
		...mapActions({
			setTaggingToolSettingsOpen: 'app/setTaggingToolSettingsOpen',
		}),
		handleSettingsClick() {
			this.setTaggingToolSettingsOpen(!this.isTaggingToolSettingsOpen);
		},
		handleMenuClick() {
			this.mobileMenuOpen = !this.mobileMenuOpen;
		},
		openHelp() {
			const actions = [];
			if (this.isCaptureTool) {
				actions.push(...MULTI_FORMAT_KEYBOARD_SHORTCUTS);
			} else if (this.isTaggingTool) {
				actions.push(...taggingToolKeyActions.map((action) => {
					return {
						key:         action.key,
						description: this.$I18n.trans(`one.keyboard_shortcuts.keys.${action.action}`),
						shiftKey:    action.shiftKey,
					};
				}));
			}
			window.eventBus.$emit(`modal:${this.modalNameKeyboardShortcuts}`, actions);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.one-header {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	padding-left: $sp4;
	padding-right: $sp4;
	height: var(--one-header-height-mobile);
	flex-shrink: 0;

	@include size(md-up) {
		height: var(--one-header-height);
	}

	// hide header on capture tool on mobile landscape
	.is-capture & {
		@include mobile-landscape {
			display: none;
		}
	}
}

.one-header__left {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: $sp6;
}

.one-header__logo {
	width: $sp5;
	height: $sp5;
}

.one-header__title {
	position: relative;
	font-size: 18px;
	font-weight: 500;
	line-height: 1;
	color: var(--color-white);

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: -#{$sp6 / 2};
		height: 100%;
		width: 2px;
		background-color: var(--color-white);
	}
}

.one-header__navigation-list {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 1px;

	@include size(sm-down) {
		@include z-index(asport-one-nav-mobile);
		display: flex;
		flex-direction: column;
		position: fixed;
		top: var(--one-header-height-mobile);
		left: 0;
		width: 100vw;
		height: calc(100vh - var(--one-header-height-mobile));
		transform: translateX(100%);
		will-change: transform;
		transition: transform $trans-time-fast;
		color: var(--color-white);
		background-color: var(--color-grey-950);

		&.is-mobile-menu-open {
			transform: translateX(0%);
		}
	}

	@include size(md-up) {
		height: 100%;
	}
}

.one-header__navigation-item {
	width: 100%;

	@include size(md-up) {
		padding: $sp3 0;
		width: auto;
		height: 100%;
	}
}

.one-header__navigation-link {
	display: inline-block;
	color: var(--color-white);
	text-decoration: none;
	padding: $sp4-5 $sp6;
	margin: 0 $sp2;
	font-weight: 500;
	width: 100%;
	transition: border-color $trans-time-fast, color $trans-time-fast;
	color: var(--color-grey-300);
	cursor: pointer;

	.one-header__navigation-item.is-active & {
		color: var(--color-white);
		border-color: var(--color-hype-yellow);
	}

	@include size(md-up) {
		width: auto;
		padding: 0 $sp6;
		height: 100%;
		display: flex;
		align-items: center;
		border-bottom: 2px solid transparent;
	}

	@media (hover: hover) {
		&:hover {
			color: var(--color-white);
			border-color: var(--color-hype-yellow);
		}
	}
}

.one-header__right {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-end;
	color: var(--color-grey-300);

	@include size(md-up) {
		min-width: 100px;
	}
}

.one-header__help-button {
	font-size: 24px;
	font-weight: 300;
}
</style>
